import type { FsAnimationEffect } from './fsanimationeffect';
import type { FsAnimationTimeline } from './fsanimationtimeline';

export class FsAnimation {
  private readonly _instance: Animation | null = null;
  private _currentTime: number | null | undefined;
  private _effect: FsAnimationEffect | null = null;
  private _id: string = '';
  private _playbackRate: number = 1;
  private _playState: AnimationPlayState = 'idle';
  private _startTime: number | null | undefined;
  private _timeline: FsAnimationTimeline | null = null;

  get instance(): Animation | null {
    return this._instance ?? null;
  }

  set currentTime(value: number | null) {
    this._currentTime = value;
  }

  set effect(value: FsAnimationEffect | null) {
    this._effect = value;
  }

  set id(value: string | null | undefined) {
    this._id = value ?? '';
  }

  set playbackRate(value: number) {
    this._playbackRate = value;
  }

  set startTime(value: number | null) {
    this._startTime = value;
  }

  set timeline(value: FsAnimationTimeline | null) {
    this._timeline = value;
  }

  constructor(effect?: FsAnimationEffect, timeline?: FsAnimationTimeline) {
    if (typeof Animation === 'undefined') {
      return;
    }

    this._instance = timeline === null ? new Animation(null, null) : new Animation(null);
    this._playbackRate = this._instance.playbackRate;
    this._playState = this._instance.playState;

    // No need to queue the next timeline if it matches the default value -- null
    if (effect) {
      this._effect = effect;
    }

    // No need to queue the next timeline if it matches the default value -- null
    if (timeline) {
      this._timeline = timeline;
    }
  }

  cancel() {
    this._playState = 'idle';
  }

  finish() {
    this._playState = 'finished';
  }

  play() {
    this._playState = 'running';
  }

  pause() {
    this._playState = 'paused';
  }

  reverse() {
    this._playbackRate = this._playbackRate * -1;
  }

  updatePlaybackRate(rate: number | undefined) {
    this._playbackRate = rate ?? 1;
  }

  updatePlayState(playState: AnimationPlayState | undefined) {
    if (playState) {
      this._playState = playState;
    }
  }

  /**
   * Apply all pending changes to the underlying DOM objects
   */
  flush() {
    const animation = this.instance;

    if (!animation) {
      return;
    }

    if (this._id !== animation.id) {
      animation.id = this._id;
    }

    if (animation.playbackRate !== this._playbackRate) {
      animation.updatePlaybackRate(this._playbackRate);
    }

    // Flush effects/timelines before getting instance references
    this._effect?.flush();
    this._timeline?.flush();

    const nextEffect = this._effect?.instance ?? null;
    const nextTimeline = this._timeline?.instance ?? null;

    if (nextEffect !== animation.effect) {
      animation.effect = nextEffect;
    }

    if (nextTimeline && nextTimeline !== animation.timeline) {
      // Only the getter is supported in certain browsers:
      // https://developer.mozilla.org/en-US/docs/Web/API/Animation/timeline
      try {
        animation.timeline = nextTimeline;
      } catch (e) {
        console.warn(
          'Unable to flush Animation.timeline. The property is readonly in this browser.',
        );
      }
    }

    // Copy and reset auto-updating currentTime only if changes were made
    if (typeof this._currentTime !== 'undefined') {
      animation.currentTime = this._currentTime;
      this._currentTime = undefined;
    }

    // Copy and reset auto-updating startTime only if changes were made
    if (typeof this._startTime !== 'undefined') {
      animation.startTime = this._startTime;
      this._startTime = undefined;
    }

    // Synchronize the playState
    if (this._playState !== animation.playState) {
      switch (this._playState) {
        case 'finished': {
          animation.finish();
          break;
        }
        case 'idle': {
          animation.cancel();
          break;
        }
        case 'paused': {
          animation.pause();
          break;
        }
        case 'running': {
          animation.play();
          break;
        }
      }
    }
  }
}
